import { css, SerializedStyles } from "@emotion/react";
import { kitText } from "../../styles";
import { ButtonSize, ButtonVariant } from ".";

// Temporary CSS variable corrections.
// These should be removed when corrected in Burns.
const burnsThemeCorrections = css`
  --font-line-height-micro: 0.8;
  --font-size-micro: 0.625rem;
  --color-background-high-contrast: hsla(53, 25%, 94%, 1);
`;

const variants: Partial<Record<ButtonVariant, SerializedStyles>> = {
  primary: css`
    --button-text-color: var(--color-contrast);
    --button-border-color: transparent;
    --button-background-color: var(--color-splash-primary);
    --button-hover-background-color: var(--color-splash-secondary);
    --button-active-background-color: var(--color-splash-tertiary);
  `,
  outline: css`
    --button-text-color: var(--color-splash-primary);
    --button-border-color: var(--button-text-color);
    --button-hover-background-color: var(--color-splash-primary);
    --button-hover-text-color: var(--color-contrast);
    --button-active-border-color: var(--color-splash-primary);
    --button-disabled-background-color: var(--color-contrast);
    --button-disabled-border-color: var(--color-border-secondary);
  `,
  basic: css`
    --button-border-color: var(--color-border-primary);
    --button-hover-background-color: var(--color-background-high-contrast);
    --button-hover-border-color: var(--color-border-primary);
    --button-active-border-color: var(--color-splash-primary);
    --button-disabled-border-color: var(--color-border-secondary);
  `,
  get round() {
    return css`
      ${this.basic}
      ${kitText.style.normal}
      --button-border-radius: calc(calc(1em / 3) + 1em);
    `;
  },
  ghost: css`
    --button-text-color: var(--color-splash-primary);
    --button-active-text-color: var(--color-splash-tertiary);
    --button-background-color: transparent;
    --button-disabled-background-color: transparent;

    padding: 0;

    &:after {
      content: none;
    }
  `
};

const sizes: Partial<Record<ButtonSize, SerializedStyles>> = {
  small: css`
    --button-padding-x: var(--spacing-8);
    --button-padding-y: var(--button-padding-x);
    --button-gap: calc(var(--button-padding-x) / 2);

    ${kitText.variant.micro}
    ${kitText.style.normal}
  `
};

export const styles = {
  base: css`
    ${burnsThemeCorrections}

    --button-background-color: var(--color-contrast);
    --button-text-color: var(--color-primary);
    --button-active-background-color: var(--button-background-color);
    --button-disabled-background-color: var(--color-background-tertiary);
    --button-disabled-text-color: var(--color-tertiary);
    --button-border-width: 0.075rem;
    --button-border-radius: var(--border-radii-base);
    --button-padding-x: var(--spacing-16);
    --button-padding-y: calc(var(--button-padding-x) / 2);
    --button-transition-time: 200ms;
    --button-icon-color: var(--button-text-color);
    --button-border-color: var(--button-background-color);

    --icon-fill-color: var(--button-icon-color);
    display: inline-flex;
    position: relative;
    white-space: nowrap;
    background-color: var(--button-background-color);
    border-radius: var(--button-border-radius);
    border: 0 solid var(--button-border-color);
    transition:
      background-color var(--button-transition-time),
      color var(--button-transition-time);
    padding: var(--button-padding-y) var(--button-padding-x);
    align-items: center;
    justify-content: center;
    gap: var(--button-gap, var(--button-padding-y));
    color: var(--button-text-color);
    cursor: pointer;
    ${kitText.variant.body}
    ${kitText.style.bold}

    &:hover {
      --icon-fill-color: var(
        --button-hover-text-color,
        var(--button-text-color)
      );
      background-color: var(--button-hover-background-color);
      border-color: var(
        --button-hover-border-color,
        var(--button-border-color)
      );
      color: var(--button-hover-text-color, var(--button-text-color));
      text-decoration: none;
    }

    &:active {
      --icon-fill-color: var(
        --button-active-text-color,
        var(--button-text-color)
      );
      background-color: var(--button-active-background-color);
      border-color: var(
        --button-active-border-color,
        var(--button-border-color)
      );
      color: var(--button-active-text-color, var(--button-text-color));
    }

    &:disabled {
      --icon-fill-color: var(--button-disabled-text-color);
      background-color: var(--button-disabled-background-color);
      border-color: var(
        --button-disabled-border-color,
        var(--button-border-color)
      );
      color: var(--button-disabled-text-color);
      cursor: default;
    }

    &:focus-visible {
      outline: var(--button-border-width) solid var(--color-primary);
      outline-offset: var(--button-border-width);
    }

    /* Pseudo-element used for border to so that the border and padding share the same total space. */
    &:after {
      content: "";
      position: absolute;
      inset: 0;
      border: var(--button-border-width) solid;
      border-color: inherit;
      border-radius: var(--button-border-radius);
      pointer-events: none;
    }
  `,
  loading: css`
    &:disabled {
      color: var(--button-disabled-background-color);

      > * {
        opacity: 0;
      }

      > .kit-Button-loading {
        opacity: 1;
        position: absolute;
      }
    }
  `,
  normalizeLineHeight: css`
    /* This makes all direct children ignore vertical padding to account for icons
    that can be taller than the calculated font height. Otherwise the padding would
    need to be adjusted based on size prop, font size, and icon size. */
    > * {
      display: inline-flex;
      align-items: center;
      height: 0;
    }
  `,
  variants,
  sizes
};
